import { ApiErrorCode } from '@orn/global/errors/apiErrorCodes.js';
import Button from '@orn/shared-web/components/Button/Button';
import { OTPInput } from '@orn/shared-web/components/Input';
import * as React from 'react';

import useAuthApi from '../../../hooks/useAuthApi';
import useCountdown from '../../../hooks/useCountdown';
import { OnboardingTitle } from '../../OnboardingLayout';
import { RESEND_COUNTDOWN_SECONDS } from '../types';

import type { OtpChannel, OtpPurpose } from '../types';

interface IOTPVerificationStepProps {
  email: string;
  userId: string;
  otpPurpose: OtpPurpose;
  otpChannel?: OtpChannel;
  phoneLast4?: string;
  onSuccess: () => void;
  onBack: () => void;
  onWaitlisted: () => void;
}

const OTPVerificationStep = ({
  email,
  userId,
  otpPurpose,
  otpChannel = 'email',
  phoneLast4: initialPhoneLast4,
  onSuccess,
  onBack,
  onWaitlisted,
}: IOTPVerificationStepProps) => {
  const authApi = useAuthApi();
  const countdown = useCountdown();
  const [otpValue, setOtpValue] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | undefined>();
  const [currentChannel, setCurrentChannel] = React.useState<OtpChannel>(otpChannel);
  const [phoneLast4, setPhoneLast4] = React.useState(initialPhoneLast4);

  // Start countdown on mount (code was just sent)
  React.useEffect(() => {
    countdown.start(RESEND_COUNTDOWN_SECONDS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleVerify = React.useCallback(
    async (code: string) => {
      setIsSubmitting(true);
      setErrorMessage(undefined);

      const verifyFn = otpPurpose === 'registration' ? authApi.verifyRegistrationOtp : authApi.verifyLoginOtp;
      const result = await verifyFn({ userId, code });

      setIsSubmitting(false);

      if (!result.ok) {
        if (result.error.code === ApiErrorCode.AUTH_NOT_ON_ALPHA_ALLOW_LIST) {
          onWaitlisted();
          return;
        }
        setErrorMessage(
          result.error.code === ApiErrorCode.AUTH_INVALID_OTP
            ? 'Invalid or expired code. Please try again.'
            : result.error.error
        );
        setOtpValue('');
        return;
      }

      onSuccess();
    },
    [authApi, otpPurpose, userId, onSuccess, onWaitlisted]
  );

  const handleResend = React.useCallback(
    async (channel?: OtpChannel) => {
      setErrorMessage(undefined);

      if (otpPurpose === 'registration') {
        const result = await authApi.resendRegistrationOtp({ userId });
        if (!result.ok) {
          setErrorMessage(
            result.error.code === ApiErrorCode.AUTH_RESEND_LIMIT
              ? 'Too many resend attempts. Please wait before trying again.'
              : result.error.error
          );
          return;
        }
      } else {
        const result = await authApi.resendLoginOtp({ userId, channel });
        if (!result.ok) {
          const msg =
            result.error.code === ApiErrorCode.AUTH_RESEND_LIMIT ||
            result.error.code === ApiErrorCode.AUTH_SMS_RATE_LIMITED
              ? 'Too many resend attempts. Please wait before trying again.'
              : result.error.error;
          setErrorMessage(msg);
          return;
        }
        if (result.data.otpChannel) {
          setCurrentChannel(result.data.otpChannel);
          setPhoneLast4(result.data.phoneLast4);
        }
      }

      countdown.start(RESEND_COUNTDOWN_SECONDS);
    },
    [authApi, otpPurpose, userId, countdown]
  );

  const handleComplete = React.useCallback(
    (code: string) => {
      void handleVerify(code);
    },
    [handleVerify]
  );

  const isLoginSms = otpPurpose === 'login' && currentChannel === 'sms';
  const title = currentChannel === 'sms' ? 'Check your phone' : 'Check your email';
  const smsBody = phoneLast4
    ? `We sent a 6-digit code to your phone ending in ${phoneLast4}. Enter it below.`
    : 'We sent a 6-digit code to your phone. Enter it below.';
  const body = currentChannel === 'sms' ? smsBody : `We sent a 6-digit code to ${email}. Enter it below.`;

  return (
    <>
      <OnboardingTitle title={title} onBack={onBack} subtitle={body} />

      <div className="flex w-full flex-col items-center gap-4">
        <OTPInput
          value={otpValue}
          onChange={setOtpValue}
          onComplete={handleComplete}
          isInvalid={Boolean(errorMessage)}
          errorMessage={errorMessage}
          isDisabled={isSubmitting}
          autoFocus
        />

        <div className="flex flex-col items-center gap-1">
          {countdown.isActive ? (
            <span className="text-tertiary text-sm">Resend code in {countdown.secondsRemaining}s</span>
          ) : (
            <>
              <Button color="link-color" size="sm" onClick={() => void handleResend()}>
                {isLoginSms ? 'Resend code via SMS' : 'Resend code'}
              </Button>
              {isLoginSms && (
                <Button color="link-color" size="sm" onClick={() => void handleResend('email')}>
                  Send via email instead
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

OTPVerificationStep.displayName = 'OTPVerificationStep';

export default OTPVerificationStep;
